import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Check, Circle, X, Rocket, Building2, Link2, Wand2 } from "lucide-react";

const PLAN_BLURB = {
  creator: "You're on Creator ($29/mo) with full access — unlimited 1-click campaigns, branded image rendering and automated dispatch. Need Shopify sync or the AI Consultant? Upgrade to E-commerce Growth anytime from Billing. Cancel anytime.",
  growth: "You're on E-commerce Growth ($79/mo) — Shopify catalog sync, the AI Strategy Consultant and priority generation are all included, on top of unlimited campaigns and automated dispatch. Cancel anytime.",
  agency: "You're on Agency ($199/mo) — everything unlocked: up to 25 Facebook & LinkedIn profiles, 10 team & client seats, 50GB storage and unlimited AI generation. Cancel anytime.",
  none: "You're on the free plan. Choose a plan from Billing to unlock unlimited 1-click campaigns, branded graphics and automated dispatch.",
};

export default function OnboardingWizard() {
  const { active, workspaces } = useWorkspace();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [dismissed, setDismissed] = useState(localStorage.getItem("gs_onboarding_done") === "1");
  const navigate = useNavigate();

  useEffect(() => {
    if (!active) return;
    api.get(`/posts?workspace_id=${active.id}`).then((r) => setPosts(r.data)).catch(() => {});
  }, [active]);

  const workspaceDone = (workspaces?.length || 0) > 0;
  const connectDone = (active?.connected_accounts?.length || 0) > 0;
  const postDone = posts.length > 0;
  const allDone = workspaceDone && connectDone && postDone;

  useEffect(() => {
    if (allDone) localStorage.setItem("gs_onboarding_done", "1");
  }, [allDone]);

  if (dismissed || !active) return null;

  const steps = [
    {
      key: "workspace", label: "Create your first workspace",
      desc: "One brand or game per workspace.",
      done: workspaceDone, icon: Building2,
      action: null, cta: "Done",
    },
    {
      key: "connect", label: "Connect a social account",
      desc: "Link an X or Facebook profile via Zernio.",
      done: connectDone, icon: Link2,
      action: () => navigate("/settings"), cta: "Open settings",
    },
    {
      key: "post", label: "Generate & queue your first post",
      desc: "Let the anti-slop engine write one for you.",
      done: postDone, icon: Wand2,
      action: () => navigate("/studio"), cta: "Open studio",
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = (doneCount / steps.length) * 100;

  if (allDone) return null;
  const finish = () => { localStorage.setItem("gs_onboarding_done", "1"); setDismissed(true); };
  const next = steps.find((s) => !s.done);

  return (
    <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-6 relative" data-testid="onboarding-wizard">
      <button onClick={finish} data-testid="onboarding-dismiss" className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
          <Rocket className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="font-display font-bold text-lg">Get set up in 3 steps</h3>
          <p className="text-xs text-muted-foreground">{doneCount} of {steps.length} complete</p>
        </div>
      </div>
      <Progress value={pct} className="h-1.5 mb-5" data-testid="onboarding-progress" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {steps.map((s) => (
          <div key={s.key} data-testid={`onboarding-step-${s.key}`}
            className={`rounded-lg border p-4 ${s.done ? "border-emerald-500/40 bg-emerald-500/5" : next?.key === s.key ? "border-primary/50 bg-primary/5" : "border-border bg-secondary/20"}`}>
            <div className="flex items-center gap-2 mb-2">
              {s.done ? <Check className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
              <s.icon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-sm font-medium mb-1">{s.label}</div>
            <div className="text-xs text-muted-foreground mb-3">{s.desc}</div>
            {!s.done && s.action && (
              <Button size="sm" variant="outline" data-testid={`onboarding-cta-${s.key}`} onClick={s.action}
                className="h-7 text-xs border-primary/40 text-primary w-full">{s.cta}</Button>
            )}
            {s.done && <span className="text-xs text-emerald-400 font-medium">Complete</span>}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-4 flex items-start gap-1.5">
        <Rocket className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
        {PLAN_BLURB[["creator", "growth", "agency"].includes(user?.plan) ? user.plan : (user?.plan === "pro" ? "creator" : "none")]}
      </p>
    </div>
  );
}
